import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "./Navbar";

/* Legal pages, Terms of Service and Privacy Policy, written for what this
   product actually does: AI-tailored resumes, assisted job applications the
   user personally submits, a credit economy, and coaching. Plain language on
   purpose: a legal page nobody can read protects nobody. */

const UPDATED = "August 16, 2026";
const CONTACT = "advisors@theresumeadvisory.com";

function LegalShell({ title, children }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <Navbar />
      <div className="bg-white min-h-screen">
        <div className="max-w-3xl mx-auto px-6 py-14">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">{title}</h1>
          <p className="mt-2 text-sm text-gray-400">Last updated: {UPDATED}</p>
          <div className="mt-8 space-y-8 text-[15px] leading-relaxed text-gray-700">
            {children}
          </div>
          <div className="mt-12 pt-6 border-t border-gray-100 text-sm text-gray-500">
            Questions? Contact us at <a className="text-blue-600 hover:underline" href={`mailto:${CONTACT}`}>{CONTACT}</a>.
            {" "}See also: <Link to="/terms" className="text-blue-600 hover:underline">Terms of Service</Link> ·{" "}
            <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </>
  );
}

const H = ({ children }) => <h2 className="text-lg font-bold text-gray-900 mb-2">{children}</h2>;

export function TermsPage() {
  return (
    <LegalShell title="Terms of Service">
      <section>
        <H>1. Who we are, and what you're agreeing to</H>
        <p>
          Resume Advisor ("we", "us") provides AI-assisted career tools: resume analysis and
          tailoring, a matched job feed, assisted job applications (Auto-Apply), and career
          coaching. By creating an account or using the service you agree to these Terms and to
          our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>.
        </p>
      </section>

      <section>
        <H>2. Your information must be true</H>
        <p>
          Everything the service writes, resumes, screening-question answers, essays, is built
          from information <em>you</em> provide. You confirm that your resume, profile answers
          (including work authorization and visa sponsorship), and any saved application answers
          are truthful and yours. Submitting false information to an employer can have serious
          consequences, and it is yours to bear: we assemble your words and facts; we do not verify them.
        </p>
      </section>

      <section>
        <H>3. AI-generated content, you review, you own the send</H>
        <p>
          Parts of the service use artificial intelligence to tailor your resume and draft answers
          to employer questions. The AI is instructed to use only your real history and never to
          invent employers, dates, metrics, or achievements, and drafted essays are always visible
          to you on the application form before anything is sent. AI output can still be imperfect.
          <strong> You are responsible for reviewing every application before you submit it.</strong>
        </p>
      </section>

      <section>
        <H>4. Auto-Apply: assisted applications</H>
        <p>
          When you click Apply, you authorize us to prepare that one application on your behalf:
          opening the employer's application form in a browser window on your device, filling it
          from your profile and resume, and attaching your resume PDF. <strong>The application is
          only ever sent when you personally press the employer's Submit button.</strong> We never
          submit for you invisibly, never solve or bypass CAPTCHAs or security checks, and never
          misrepresent who is applying, the applicant is you.
        </p>
        <p className="mt-2">
          Applications are submitted to third-party hiring systems (such as Greenhouse, Lever, or
          Ashby) and to the employers behind them. Once you submit, the employer's own terms and
          privacy practices govern what they do with your application. We are not affiliated with,
          or endorsed by, those platforms or employers.
        </p>
      </section>

      <section>
        <H>5. Credits and payments</H>
        <p>
          Auto-Apply uses prepaid credits. <strong>One credit is charged only when an application
          is actually sent</strong> (you pressed Submit, or a submission was completed and
          acknowledged). If an application fails before sending, or you close the window without
          submitting, the held credit is automatically returned. Credits are tied to your account,
          are not transferable, and are not redeemable for cash except where required by law.
          Payments are processed by Stripe; we never see or store your full card number.
        </p>
      </section>

      <section>
        <H>6. Acceptable use</H>
        <p>You agree not to: use the service to submit false or misleading information; apply on
          behalf of anyone other than yourself; attempt to bypass employer security measures;
          scrape, resell, or abuse the service; or use it for any unlawful purpose. We may suspend
          accounts that do.</p>
      </section>

      <section>
        <H>7. No employment guarantee</H>
        <p>
          We improve the odds and remove the grind, we do not and cannot promise interviews,
          offers, or employment. Hiring decisions belong to employers alone.
        </p>
      </section>

      <section>
        <H>8. Disclaimers and limitation of liability</H>
        <p>
          The service is provided "as is" without warranties of any kind, express or implied. To
          the maximum extent permitted by law, our total liability for any claim arising from the
          service is limited to the amount you paid us in the twelve months before the claim. We
          are not liable for decisions made by employers, for the conduct of third-party platforms,
          or for indirect or consequential damages.
        </p>
      </section>

      <section>
        <H>9. Termination, changes, and contact</H>
        <p>
          You may stop using the service and request account deletion at any time. We may update
          these Terms; material changes will be posted here with a new "Last updated" date, and
          continued use after that constitutes acceptance. These Terms are governed by the laws of
          the State of Michigan, USA. Contact: {CONTACT}.
        </p>
      </section>
    </LegalShell>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy">
      <section>
        <H>1. What we collect</H>
        <p>We collect what the service needs to do its job, and nothing hidden:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li><strong>Account data</strong>, name, email, sign-in credentials (or your Google/GitHub identity).</li>
          <li><strong>Career data</strong>, your uploaded resume, AI-tailored versions of it, and your application profile (contact details, work authorization, sponsorship needs, preferences, your "about you" text).</li>
          <li><strong>Application data</strong>, answers you save to employer questions, the record of each application (job, status, fields filled), and a screenshot of the application page's final state, kept as your receipt of what happened.</li>
          <li><strong>Payment data</strong>, handled by Stripe; we store transaction records, never your full card number.</li>
        </ul>
      </section>

      <section>
        <H>2. How we use it</H>
        <p>
          To tailor your resume to jobs, score your fit, rank the job feed for you, fill the
          applications you choose to send, notify you how they resolved, and provide coaching.
          We do not sell your personal information. Ever.
        </p>
      </section>

      <section>
        <H>3. AI processing</H>
        <p>
          Resume text, job descriptions, and employer questions are processed by a third-party AI
          provider (currently OpenAI) to tailor your resume and draft answers grounded in your own
          information. We send only what the task needs. We do not use your data to train AI
          models.
        </p>
      </section>

      <section>
        <H>4. Who receives your data</H>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li><strong>Employers and their hiring systems</strong> (Greenhouse, Lever, Ashby), only when <em>you</em> submit an application; from then on their privacy policies govern.</li>
          <li><strong>Service providers</strong>, Stripe (payments), our AI provider (drafting), and our email provider (notifications), each only what their function requires.</li>
          <li><strong>No one else.</strong> No data brokers, no advertisers.</li>
        </ul>
      </section>

      <section>
        <H>5. Sensitive information, our strictest rule</H>
        <p>
          We never infer, store, or answer demographic or protected-class information (race,
          gender, veteran status, disability, sexual orientation) on your behalf. If you enable the
          optional setting, the only thing the service will ever select for an EEO question is the
          form's own "prefer not to say" option. Disability and accommodation questions are always
          left for you personally. Consent and privacy-notice checkboxes on applications are ticked
          only under your explicit standing opt-in.
        </p>
      </section>

      <section>
        <H>6. Retention and deletion</H>
        <p>
          Your data is kept while your account is active so your history and receipts stay
          available to you. Email {CONTACT} to request access to, correction of, or deletion of
          your data; deletion removes your account, profile, resumes, application records, and
          screenshots, except records we must keep for payment and legal compliance.
        </p>
      </section>

      <section>
        <H>7. Security and cookies</H>
        <p>
          Data is transmitted over HTTPS and access is restricted. We use cookies only to keep you
          signed in and to remember your session, no third-party advertising cookies. No system is
          perfectly secure; we notify affected users of any breach as required by law.
        </p>
      </section>

      <section>
        <H>8. Children and changes</H>
        <p>
          The service is not directed to anyone under 16, and we do not knowingly collect their
          data. Material changes to this policy are posted here with a new "Last updated" date.
          Contact: {CONTACT}.
        </p>
      </section>
    </LegalShell>
  );
}
